import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from './api';
import type { PublishLog } from './types';

// La publicación es asíncrona: POST /api/publish solo dispara el build+deploy
// (corre en CI y tarda minutos) y devuelve enseguida con estado "pendiente".
// Un callback marca el registro como exitoso/con error al terminar. Por eso no
// podemos declarar éxito al recibir respuesta: hay que sondear el registro.
const POLL_INTERVAL_MS = 5_000;

export type PublishPhase = 'idle' | 'publishing' | 'done' | 'error';

/** Detalle de por qué el contenido no pasó la validación previa a publicar (422). */
export interface PublishIssue {
  message: string;
  /** Título de la página afectada, si se pudo resolver contra /api/pages. */
  pageTitle: string | null;
}

export interface UsePublish {
  /** Publicaciones registradas; la última del array es la más reciente. */
  log: PublishLog[];
  /** Publicación más reciente, o null si nunca se ha publicado. */
  last: PublishLog | null;
  /** Hay un deploy en curso (el último del registro está pendiente). */
  pending: boolean;
  /** Fase de la publicación iniciada desde esta vista con publish(). */
  phase: PublishPhase;
  /** Mensaje al usuario si la publicación falló. */
  error: string | null;
  /** Detalle por página cuando el error viene de contenido sin resolver (422). */
  issues: PublishIssue[];
  /** Dispara una publicación y empieza a seguir su progreso. */
  publish: () => Promise<void>;
  /** Dispara una publicación y devuelve su identificador para un flujo que necesita seguirla. */
  publishWithId: () => Promise<number>;
  /** Empieza a seguir una publicación iniciada por otro flujo, como onboarding. */
  track: (logId: number) => Promise<void>;
  /** Recarga el registro de publicaciones. */
  refresh: () => Promise<void>;
  /** Limpia el error y vuelve a idle (para cerrar modales de error). */
  clearError: () => void;
}

export function translatePublishError(msg: string): string {
  if (msg.includes('No deploy method configured')) {
    return 'Este sitio no tiene un método de publicación configurado. Ve a Configuración → General para asignarlo.';
  }
  if (msg.includes('Tenant not found')) {
    return 'No se encontró el sitio en el sistema. Recarga la página e inténtalo de nuevo.';
  }
  if (msg.includes('repository_dispatch') || msg.includes('Deploy hook')) {
    return 'No se pudo iniciar el proceso de publicación en el servidor. Inténtalo de nuevo en un momento.';
  }
  return msg || 'No se pudo publicar el sitio. Inténtalo de nuevo en un momento.';
}

/**
 * `POST /api/publish` reporta el contenido sin resolver con un `path` del tipo
 * `page:<id>[0].Block.props.campo` (ver `content-validation.ts`). Aquí solo nos
 * interesa el id de página, para mostrar su título en vez de un identificador técnico.
 */
function extractPageId(path: string): string | null {
  const match = /^page:([^[]+)/.exec(path);
  return match ? match[1] : null;
}

async function resolvePublishIssues(rawIssues: { message: string; path: string }[]): Promise<PublishIssue[]> {
  if (rawIssues.length === 0) return [];
  const titleById = new Map<string, string>();
  try {
    const pages = await api.pages.list();
    for (const page of pages) titleById.set(page.id, page.title);
  } catch {
    // Si no se puede resolver el título, el mensaje solo se muestra sin página.
  }
  return rawIssues.map((issue) => {
    const pageId = extractPageId(issue.path);
    return { message: issue.message, pageTitle: pageId ? titleById.get(pageId) ?? null : null };
  });
}

/**
 * Única instancia real del estado de publicación — vive en PublishProvider,
 * montado una vez en la raíz de la app. Antes cada página montaba su propio
 * usePublish(), así que un deploy en curso "desaparecía" (botón reactivado,
 * banner sin aviso) en cuanto se navegaba a otra vista con su propia instancia.
 */
function usePublishState(): UsePublish {
  const [log, setLog] = useState<PublishLog[]>([]);
  const [phase, setPhase] = useState<PublishPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<PublishIssue[]>([]);
  const trackedId = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    const rows = await api.publish.log().catch(() => null);
    if (rows) setLog(rows);
  }, []);

  useEffect(() => { void Promise.resolve().then(() => refresh()); }, [refresh]);

  const last = log.length ? log[log.length - 1] : null;
  const pending = last?.status === 'pending';

  const track = useCallback(async (logId: number) => {
    trackedId.current = logId;
    setPhase('publishing');
    await refresh();
  }, [refresh]);

  const publishWithId = useCallback(async () => {
    setError(null);
    setIssues([]);
    try {
      const { logId } = await api.publish.trigger();
      await track(logId);
      return logId;
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      const translated = translatePublishError(raw);
      setError(translated);
      const rawIssues = (err as { issues?: { message: string; path: string }[] } | undefined)?.issues ?? [];
      setIssues(await resolvePublishIssues(rawIssues));
      setPhase('error');
      throw Object.assign(new Error(translated), { cause: err });
    }
  }, [track]);

  const publish = useCallback(async () => {
    await publishWithId();
  }, [publishWithId]);

  const clearError = useCallback(() => {
    setError(null);
    setIssues([]);
    setPhase('idle');
  }, []);

  // Sondea mientras haya un deploy en curso —para reflejar su estado— o
  // mientras seguimos el que iniciamos aquí. Solo confirmamos el resultado
  // cuando el registro lo dice; no antes.
  useEffect(() => {
    const tracking = phase === 'publishing';
    if (!pending && !tracking) return;
    const interval = setInterval(async () => {
      const rows = await api.publish.log().catch(() => null);
      if (!rows) return;
      setLog(rows);
      const watched = tracking && trackedId.current != null
        ? rows.find((row) => row.id === trackedId.current)
        : rows[rows.length - 1];
      if (watched?.status === 'success') {
        if (tracking) setPhase('done');
        clearInterval(interval);
      } else if (watched?.status === 'error') {
        if (tracking) {
          setError(translatePublishError(watched.errorMsg ?? ''));
          setPhase('error');
        }
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pending, phase]);

  return { log, last, pending, phase, error, issues, publish, publishWithId, track, refresh, clearError };
}

const PublishContext = createContext<UsePublish | null>(null);

/** Monta la única instancia de estado de publicación de toda la app. */
export function PublishProvider({ children }: { children: ReactNode }) {
  const value = usePublishState();
  return createElement(PublishContext.Provider, { value }, children);
}

/**
 * Estado de publicación compartido por toda la app (Home, Páginas, Mi Sitio,
 * Editor…). Siempre lee la misma instancia vía contexto, así que un deploy en
 * curso se ve igual sin importar desde qué vista se disparó o a cuál se navegó.
 */
export function usePublish(): UsePublish {
  const ctx = useContext(PublishContext);
  if (!ctx) throw new Error('usePublish() requiere <PublishProvider> en la raíz de la app');
  return ctx;
}
